"use client";

import { useCallback, useState } from "react";
import { FullScreenButton, Image } from "@pamperedchef/design-system";
import { ImageGalleryModal } from "@/composites/foundation/ImageGalleryModal";
import * as styles from "./ProductGallery.module.scss";

/**
 * Public contract — defined HERE in the composite, not imported from
 * `domain/catalog`. The composite is intentionally decoupled from the domain
 * shape so it can be reused by:
 *   - PDP (today): template maps `lifestyle_image + hero_image (silo) + media_gallery` → slides
 *   - Search / PLP / Category result drawers (future)
 *   - Recipe Detail "shop the recipe" expander (future)
 *   - consumer-web-elements (future package extraction)
 *
 * Each consumer is responsible for producing the slide list in the order it
 * wants displayed; ProductGallery is presentational only.
 */
export interface ProductGallerySlide {
	/** Resolved source URL (pre-resolved from Widen / Acquia / etc.). */
	src: string;
	alt: string;
	caption?: string;
	/** Poster URL for video slides. */
	poster?: string;
	/** Discriminator. Defaults to "image". */
	kind?: "image" | "video";
	/**
	 * Stable DAM asset identifier, pre-extracted by the consumer (the
	 * composite never parses URLs). Surfaced back through the analytics
	 * callbacks as the event's `asset_id`.
	 */
	assetId?: string;
}

export interface ProductGalleryProps {
	slides: ProductGallerySlide[];
	/** Used in modal title and empty-state aria-label. */
	productTitle?: string;
	/**
	 * Fired whenever the user clicks a media image: the hero / desktop tile
	 * (which also opens the fullscreen gallery) and mobile thumbnail taps
	 * (which only swap the hero — the gallery does not open on mobile). NOT
	 * fired for navigation inside the modal. `position` is the 1-based slot of
	 * the clicked slide.
	 */
	onMediaClick?: (slide: ProductGallerySlide, position: number) => void;
	/**
	 * Fired the first time a video slide starts playing per modal selection.
	 * `position` is the 1-based slot of the video slide.
	 */
	onVideoPlay?: (slide: ProductGallerySlide, position: number) => void;
}

/**
 * PDP hero gallery.
 *
 * Two distinct layouts driven by viewport (CSS-only, no JS-side detection):
 *  - **Mobile (< lg)**: single hero image + horizontal thumbnail strip.
 *    Tapping a thumb swaps the hero. Tapping the hero opens the modal.
 *  - **Desktop (lg+)**: vertical stack — first slide full-width, then a
 *    2-column CSS grid of the remaining slides. Each tile clickable
 *    individually to open the modal at that index.
 *
 * Both layouts open the same `ImageGalleryModal` for zoom (hover-zoom on
 * desktop, pinch-zoom on mobile).
 *
 * Built from `@pamperedchef/design-system` primitives:
 *  - `Image`     — every product photo (gives DS aspect-ratio + object-fit handling)
 *  - `FullScreenButton` — corner expand affordance on each tile / hero
 *
 * Layer rules (see `docs/PDP_ARCHITECTURE.md`):
 *  - Pure UI; no `next/*` / `app/*` / `infrastructure/*` imports
 *  - Plugin-agnostic — never references Widen / Acquia
 */
export function ProductGallery({
	slides,
	productTitle,
	onMediaClick,
	onVideoPlay
}: ProductGalleryProps) {
	const [activeIndex, setActiveIndex] = useState(0);
	const [modalOpen, setModalOpen] = useState(false);
	const [modalIndex, setModalIndex] = useState(0);

	const openModalAt = useCallback(
		(i: number) => {
			setModalIndex(i);
			setModalOpen(true);
			const slide = slides[i];
			if (slide) onMediaClick?.(slide, i + 1);
		},
		[slides, onMediaClick]
	);

	const selectThumbnail = useCallback(
		(i: number) => {
			setActiveIndex(i);
			const slide = slides[i];
			if (slide) onMediaClick?.(slide, i + 1);
		},
		[slides, onMediaClick]
	);

	const handleVideoPlay = useCallback(
		(i: number) => {
			const slide = slides[i];
			if (slide) onVideoPlay?.(slide, i + 1);
		},
		[slides, onVideoPlay]
	);

	if (slides.length === 0) {
		return (
			<div
				className={styles["product-gallery--empty"]}
				role="img"
				aria-label={
					productTitle
						? `No images available for ${productTitle}`
						: "No images available"
				}
			>
				<span>No images available</span>
			</div>
		);
	}

	const active = slides[activeIndex] ?? slides[0]!;
	const [first, ...rest] = slides;

	return (
		<div
			className={styles["product-gallery"]}
			data-component="ProductGallery"
		>
			{/* ---------- Mobile layout (< lg) ---------- */}
			<div className={styles["product-gallery__mobile"]}>
				<button
					type="button"
					className={styles["product-gallery__hero"]}
					aria-label={`Open ${active.alt} in fullscreen`}
					onClick={() => openModalAt(activeIndex)}
				>
					<Image
						src={active.poster ?? active.src}
						alt={active.alt}
						objectFit="cover"
						className={styles["product-gallery__hero-img"]}
					/>
					<span
						className={styles["product-gallery__expand"]}
						aria-hidden
					>
						<FullScreenButton state="collapsed" size="sm" />
					</span>
				</button>

				{slides.length > 1 ? (
					<ul
						className={styles["product-gallery__thumbs"]}
						role="tablist"
						aria-label="Gallery thumbnails"
					>
						{slides.map((s, i) => (
							<li
								key={`${s.src}-${i}`}
								className={
									styles["product-gallery__thumb-item"]
								}
							>
								<button
									type="button"
									role="tab"
									aria-selected={i === activeIndex}
									aria-label={`View slide ${i + 1}: ${s.alt}`}
									onClick={() => selectThumbnail(i)}
									className={[
										styles["product-gallery__thumb"],
										i === activeIndex
											? styles[
													"product-gallery__thumb--active"
												]
											: ""
									]
										.filter(Boolean)
										.join(" ")}
								>
									<Image
										src={s.poster ?? s.src}
										alt=""
										objectFit="cover"
									/>
								</button>
							</li>
						))}
					</ul>
				) : null}
			</div>

			{/* ---------- Desktop layout (lg+): vertical stack with 2-up grid ---------- */}
			<div className={styles["product-gallery__desktop"]}>
				{first ? (
					<GalleryTile
						slide={first}
						priority
						onClick={() => openModalAt(0)}
					/>
				) : null}
				{rest.length > 0 ? (
					<div className={styles["product-gallery__grid"]}>
						{rest.map((s, i) => (
							<GalleryTile
								key={`${s.src}-${i + 1}`}
								slide={s}
								onClick={() => openModalAt(i + 1)}
							/>
						))}
					</div>
				) : null}
			</div>

			<ImageGalleryModal
				open={modalOpen}
				slides={slides}
				initialIndex={modalIndex}
				title={productTitle}
				onClose={() => setModalOpen(false)}
				onVideoPlay={handleVideoPlay}
			/>
		</div>
	);
}

interface GalleryTileProps {
	slide: ProductGallerySlide;
	onClick: () => void;
	priority?: boolean;
}

function GalleryTile({ slide, onClick }: GalleryTileProps) {
	return (
		<button
			type="button"
			className={styles["product-gallery__tile"]}
			aria-label={`Open ${slide.alt} in fullscreen`}
			onClick={onClick}
		>
			<Image
				src={slide.poster ?? slide.src}
				alt={slide.alt}
				objectFit="cover"
				className={styles["product-gallery__tile-img"]}
			/>
			<span className={styles["product-gallery__expand"]} aria-hidden>
				<FullScreenButton state="collapsed" size="sm" />
			</span>
		</button>
	);
}
